'use client';

import { useState, useEffect, useCallback } from 'react';
import { portfolioApi } from '@/lib/api';
import { Globe, Play, Square, ExternalLink, Loader2, CheckCircle2, Image as ImageIcon, RefreshCw, ShieldCheck } from 'lucide-react';
import { usePersistedState } from '@/lib/usePersistedState';

interface SiteInfo {
  slug: string;
  domain: string;
  port: number | null;
  running: boolean;
  deployed: boolean;
  liveUrl: string | null;
  screenshot: string | null;
  nextVersion: string | null;
  qaScore: string | null;
  buildStatus: string | null;
  path: string;
}

export default function PortfolioPage() {
  const [sites, setSites] = useState<SiteInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState<string | null>(null);
  const [expandedSite, setExpandedSite] = usePersistedState<string | null>('portfolio:expanded', null);

  const loadSites = useCallback(() => {
    portfolioApi.list()
      .then((data: any) => { setSites(data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => { loadSites(); }, [loadSites]);

  const startSite = useCallback(async (slug: string) => {
    setActing(slug);
    try {
      const data = await portfolioApi.start(slug, 0);
      if (data.url) window.open(data.url, '_blank');
      // Refresh after a few seconds
      setTimeout(loadSites, 3000);
    } catch {}
    setActing(null);
  }, [loadSites]);

  const stopSite = useCallback(async (slug: string) => {
    setActing(slug);
    try {
      await fetch('/api/portfolio/start', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ slug, action: 'stop' }),
      });
      setTimeout(loadSites, 1000);
    } catch {}
    setActing(null);
  }, [loadSites]);

  const screenshotSrc = (site: SiteInfo): string | null => {
    if (!site.screenshot) return null;
    if (site.screenshot.startsWith('/')) return site.screenshot;
    return `/api/portfolio/screenshot?path=${encodeURIComponent(site.screenshot)}`;
  };

  const runningSites = sites.filter(s => s.running);
  const deployedSites = sites.filter(s => s.deployed);

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center h-96">
        <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
      </div>
    );
  }

  return (
    <div className="p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-emerald-500 to-cyan-600">
            <Globe className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-white">Portfolio</h1>
            <p className="text-xs text-gray-500">
              {sites.length} websites · {deployedSites.length} deployed · {runningSites.length} running
            </p>
          </div>
        </div>
        <button onClick={loadSites}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs text-gray-400 border border-white/[0.08] hover:border-gray-700 hover:text-white transition-all">
          <RefreshCw className="h-3.5 w-3.5" /> Refresh
        </button>
      </div>

      {/* Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {sites.map(site => {
          const isActing = acting === site.slug;
          const isExpanded = expandedSite === site.slug;
          const src = screenshotSrc(site);

          return (
            <div key={site.slug}
              className="group glass-card overflow-hidden hover:border-gray-700 transition-all">

              {/* Screenshot */}
              <div className="relative h-44 bg-[rgb(var(--background))] cursor-pointer"
                onClick={() => setExpandedSite(isExpanded ? null : site.slug)}>
                {src ? (
                  <img src={src} alt={site.domain}
                    className="w-full h-full object-cover object-top opacity-80 hover:opacity-100 transition-opacity"
                    onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="h-10 w-10 text-gray-800" />
                  </div>
                )}
                {/* Badges — top right */}
                <div className="absolute top-3 right-3 flex items-center gap-1.5">
                  {site.deployed && (
                    <a href={site.liveUrl!} target="_blank" rel="noopener noreferrer"
                      onClick={e => e.stopPropagation()}
                      className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 hover:bg-emerald-500/30 transition-colors"
                      title={`Live at ${site.liveUrl}`}>
                      <ShieldCheck className="h-3 w-3 text-emerald-400" />
                      <span className="text-[10px] text-emerald-400 font-medium">HTTPS</span>
                    </a>
                  )}
                  {site.running && (
                    <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-green-500/20 border border-green-500/30">
                      <div className="w-2 h-2 rounded-full bg-green-400 animate-pulse" />
                      <span className="text-[10px] text-green-400 font-medium">DEV</span>
                    </div>
                  )}
                </div>
                {/* QA Score */}
                {site.qaScore && (
                  <div className="absolute top-3 left-3 px-2 py-1 rounded-full bg-[#0d1117]/85 border border-gray-700">
                    <span className="text-[10px] text-gray-300 font-medium">QA {site.qaScore}/10</span>
                  </div>
                )}
              </div>

              {/* Info */}
              <div className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-sm font-semibold text-white">{site.domain}</h3>
                  {site.port && (
                    <span className="text-[10px] font-mono text-gray-600">:{site.port}</span>
                  )}
                </div>

                {/* Actions */}
                <div className="flex gap-2">
                  {site.running ? (
                    <>
                      <button onClick={() => window.open(`http://localhost:${site.port}`, '_blank')}
                        className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 transition-all">
                        <ExternalLink className="h-3 w-3" /> Open
                      </button>
                      <button onClick={() => stopSite(site.slug)} disabled={isActing}
                        className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-all disabled:opacity-50">
                        {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Square className="h-3 w-3" />} Stop
                      </button>
                    </>
                  ) : (
                    <button onClick={() => startSite(site.slug)} disabled={isActing}
                      className="flex-1 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium bg-cyan-500/10 text-cyan-400 border border-cyan-500/20 hover:bg-cyan-500/20 transition-all disabled:opacity-50">
                      {isActing ? <Loader2 className="h-3 w-3 animate-spin" /> : <Play className="h-3 w-3" />} Start
                    </button>
                  )}
                </div>
              </div>

              {/* Expanded detail */}
              {isExpanded && (
                <div className="border-t border-white/[0.04] p-4 bg-[rgb(var(--background))]/50 space-y-2">
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <span>Next.js {site.nextVersion || '?'}</span>
                    <span className={`px-2 py-0.5 rounded-full text-[10px] ${
                      site.buildStatus === 'complete' ? 'bg-green-500/10 text-green-400' :
                      site.buildStatus === 'error' ? 'bg-red-500/10 text-red-400' :
                      site.buildStatus === 'running' ? 'bg-cyan-500/10 text-cyan-400' :
                      'bg-gray-800 text-gray-500'
                    }`}>
                      {site.buildStatus || 'unknown'}
                    </span>
                  </div>
                  <p className="text-[10px] text-gray-600 font-mono truncate">{site.path}</p>
                  {site.running && site.port && (
                    <a href={`/projects/${site.slug}`}
                      className="block w-full text-center px-3 py-2 rounded-lg text-xs text-gray-400 border border-white/[0.08] hover:border-gray-700 hover:text-white transition-all">
                      View Project →
                    </a>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {sites.length === 0 && (
        <div className="text-center py-20">
          <Globe className="h-12 w-12 mx-auto text-gray-700 mb-4" />
          <p className="text-gray-500">No websites found</p>
        </div>
      )}
    </div>
  );
}
